import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Bell, ChevronDown, User, Settings, LogOut } from 'lucide-react';
import api from '../../services/api';

interface AppNavProps {
  title: string;
  subtitle?: string;
}

function useUnreadCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get('/notifications/unread-count')
      .then((res) => setCount(res.data?.count ?? 0))
      .catch(() => setCount(0));
  }, []);
  return count;
}

export const AppNav: React.FC<AppNavProps> = ({ title, subtitle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const unreadCount = useUnreadCount();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = user?.name
    ? user.name.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()
    : 'U';

  return (
    <header style={{
      height: '60px',
      background: 'var(--surface)',
      borderBottom: '1px solid var(--border)',
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
      zIndex: 9,
    }}>
      <div>
        <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text-1)' }}>{title}</h1>
        {subtitle && (
          <p style={{ fontSize: '12.5px', color: 'var(--text-2)', margin: 0, marginTop: '1px' }}>{subtitle}</p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Notifications */}
        <button
          onClick={() => navigate('/notifications')}
          title="Notifications"
          style={{
            position: 'relative',
            background: 'transparent',
            border: '1.5px solid var(--border)',
            borderRadius: '8px',
            cursor: 'pointer',
            padding: '6px',
            display: 'flex',
            alignItems: 'center',
            color: 'var(--text-2)',
            transition: 'all .15s',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = 'var(--p)';
            el.style.color = 'var(--p)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLElement;
            el.style.borderColor = 'var(--border)';
            el.style.color = 'var(--text-2)';
          }}
        >
          <Bell size={17} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              background: 'var(--err)',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 700,
              borderRadius: '50%',
              minWidth: '15px',
              height: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 2px',
              lineHeight: 1,
              border: '2px solid var(--surface)',
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Profile dropdown */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: '1.5px solid var(--border)',
              borderRadius: '8px',
              cursor: 'pointer',
              padding: '5px 10px 5px 5px',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--p)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'var(--p-light)',
              color: 'var(--p)',
              fontWeight: 700,
              fontSize: '12px',
              flexShrink: 0,
            }}>
              {initials}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)' }}>{user?.name}</span>
            <ChevronDown size={14} color="var(--text-2)" style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          </button>

          {dropdownOpen && (
            <div className="dropdown-menu" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0 }}>
              <button
                className="dropdown-item"
                onClick={() => { setDropdownOpen(false); navigate('/my-account'); }}
              >
                <User size={14} />
                My Account
              </button>
              <button
                className="dropdown-item"
                onClick={() => { setDropdownOpen(false); navigate('/settings'); }}
              >
                <Settings size={14} />
                Settings
              </button>
              <div className="dropdown-sep" />
              <button
                className="dropdown-item danger"
                onClick={() => { setDropdownOpen(false); logout(); }}
              >
                <LogOut size={14} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
